import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ConfigProvider, theme } from 'antd';
import "./styles/App.css";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingContactMenu from "./components/FloatingContactMenu";
import Login from "./components/Login";
import Signup from "./components/Signup";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import { ensureMetaTag, ensureCanonicalLink, setPageJsonLd } from "./utils/seoDom";

// Pages
import Home from "./pages/Home";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import Domoelec from "./pages/Domoelec";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import BrandPromotion from "./pages/BrandPromotion";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import MentionsLegales from "./pages/MentionsLegales";
import PolitiqueConfidentialite from "./pages/PolitiqueConfidentialite";
// Admin
import AdminApp from "./admin/App";

const SITE_URL = "https://www.domoelec.ma";
const DEFAULT_TITLE = "DOMOELEC | Société courant faible, détection incendie, sécurité et domotique Maroc";
const DEFAULT_DESCRIPTION = "DOMOELEC, société de courant faible au Maroc basée à Casablanca : détection incendie, vidéosurveillance, contrôle d'accès, intrusion, domotique, éclairage sécurité, réseaux et maintenance. Installation et accompagnement projet.";

const ROUTE_SEO = {
  "/": {
    title: "DOMOELEC | Société courant faible, détection incendie, vidéosurveillance et domotique Maroc",
    description: "Entreprise domotique et courant faible à Casablanca (Maroc) : installation, maintenance et conseil en détection incendie, vidéosurveillance, contrôle d'accès, intrusion et sécurité électronique. Demande de devis en ligne.",
  },
  "/domoelec": {
    title: "DOMOELEC | Entreprise courant faible Casablanca et expertise sécurité électronique",
    description: "DOMOELEC est une société marocaine spécialisée en courant faible et sécurité électronique, basée à Casablanca. Installation de détection incendie, vidéosurveillance, contrôle d'accès, domotique et onduleurs sur tout le Maroc.",
  },
  "/produits": {
    title: "Catalogue DOMOELEC | Matériel courant faible, sécurité incendie et vidéosurveillance",
    description: "Catalogue DOMOELEC : plus de 700 références en détection incendie, éclairage sécurité, extinction automatique, onduleurs, vidéosurveillance, intrusion, contrôle d'accès, domotique, réseaux, interphonie et audio professionnel.",
  },
  "/blog": {
    title: "Blog DOMOELEC | Actualités courant faible, sécurité incendie et domotique Maroc",
    description: "Actualités, conseils techniques, études de cas et réalisations DOMOELEC : détection incendie, vidéosurveillance, contrôle d'accès, alarme intrusion, domotique, onduleurs et bonnes pratiques de sécurité.",
  },
  "/services": {
    title: "Services DOMOELEC | Audit, installation, maintenance, contrôle et conseil courant faible Maroc",
    description: "DOMOELEC propose l'audit, l'étude, l'installation, la maintenance, le contrôle réglementaire et le conseil en courant faible, sécurité incendie, vidéosurveillance, contrôle d'accès et domotique au Maroc.",
  },
  "/contact": {
    title: "Contact DOMOELEC | Devis détection incendie, vidéosurveillance, contrôle d'accès Maroc",
    description: "Contactez DOMOELEC pour un devis ou une étude de projet : installation de détection incendie, vidéosurveillance, contrôle d'accès, intrusion ou domotique à Casablanca et partout au Maroc.",
  },
  "/mentions-legales": {
    title: "Mentions légales | DOMOELEC SARL Casablanca",
    description: "Mentions légales de DOMOELEC, société de courant faible et sécurité électronique basée à Casablanca, Maroc.",
  },
  "/politique-confidentialite": {
    title: "Politique de confidentialité | DOMOELEC",
    description: "Politique de protection des données personnelles du site DOMOELEC, société de courant faible, sécurité et domotique au Maroc.",
  },
};

function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname || "/";

    // /produits : les métadonnées (canonical, titre, description, robots,
    // JSON-LD) sont gérées intégralement par Products.js — un seul
    // gestionnaire pour que les balises serveur ne soient pas écrasées
    // (ex. /produits?famille=X et ?category=X ont leur canonical propre).
    if (pathname === "/produits") return;

    const isPromotionPage = pathname.startsWith("/promotion-marque/");

    let seo = ROUTE_SEO[pathname] || {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
    };
    let canonicalUrl = `${SITE_URL}${pathname}`;
    let robotsValue = "index,follow";

    if (pathname.startsWith("/produit/")) {
      seo = {
        title: "Produit | DOMOELEC",
        description: "Découvrez les détails techniques du produit DOMOELEC et demandez votre devis pour vos projets en courant faible et sécurité électronique.",
      };
    }

    if (pathname.startsWith("/promotion-marque/")) {
      seo = {
        title: "Promotion marque | DOMOELEC",
        description: "Découvrez les promotions et solutions de marque proposées par DOMOELEC pour vos projets en courant faible et sécurité électronique.",
      };
      robotsValue = "noindex,follow";
    }

    if (pathname.startsWith("/blog/")) {
      seo = {
        title: "Article de blog | DOMOELEC",
        description: "Consultez un article du blog DOMOELEC sur le courant faible, la sécurité électronique, les solutions techniques et les réalisations.",
      };
    }

    if (isPromotionPage) {
      robotsValue = "noindex,follow";
    }

    // Nettoie les données structurées de la page précédente puis
    // réinstalle le bloc Organization commun.
    setPageJsonLd([]);

    document.title = seo.title;
    ensureMetaTag("name", "description").setAttribute("content", seo.description);
    ensureMetaTag("property", "og:title").setAttribute("content", seo.title);
    ensureMetaTag("property", "og:description").setAttribute("content", seo.description);
    ensureMetaTag("name", "twitter:title").setAttribute("content", seo.title);
    ensureMetaTag("name", "twitter:description").setAttribute("content", seo.description);
    ensureMetaTag("name", "robots").setAttribute("content", robotsValue);
    ensureMetaTag("property", "og:type").setAttribute("content", "website");
    ensureMetaTag("property", "og:url").setAttribute("content", canonicalUrl);
    ensureMetaTag("property", "og:site_name").setAttribute("content", "DOMOELEC");
    ensureMetaTag("name", "twitter:card").setAttribute("content", "summary_large_image");
    ensureCanonicalLink().setAttribute("href", canonicalUrl);
  }, [location.pathname, location.search]);

  return null;
}

// Menu de contact flottant (appel, WhatsApp, e-mail, formulaire)
// affiché sur toutes les pages du site.
function FloatingWidgets() {
  return <FloatingContactMenu />;
}

function App() {
  return (
    <AuthProvider>
      <ConfigProvider
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: { colorPrimary: '#1677ff' }
        }}
      >
        <Router>
        <SeoManager />
        <ScrollToTop />
        <div className="App">
          <Routes>
            {/* Redirect legacy /dashboard to admin dashboard */}
            <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />

            {/* Routes d'authentification */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            
            {/* Routes protégées admin */}
            <Route path="/admin/*" element={
              <ProtectedRoute requiredRole="editor">
                <AdminApp />
              </ProtectedRoute>
            } />
            
            {/* Routes du site principal */}
            <Route path="/*" element={
              <>
                <Header />
                <main>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogDetail />} />
                    <Route path="/domoelec" element={<Domoelec />} />
                    <Route path="/produits" element={<Products />} />
                    <Route path="/produit/:slug" element={<ProductDetail />} />
                    <Route path="/promotion-marque/:brandName" element={<BrandPromotion />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/mentions-legales" element={<MentionsLegales />} />
                    <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
                  </Routes>
                </main>
                <Footer />
                <FloatingWidgets />
              </>
            } />
          </Routes>
        </div>
        </Router>
      </ConfigProvider>
    </AuthProvider>
  );
}

export default App;
